import { DateTime } from "luxon";

import { FormatType, IEbayOffer } from "../../models/EbayOffer";
import { IPart } from "../../models/Part";
import { IListingPriceSummary } from "../../models/listingOptimisation/IListingInsights";
import checkEbayDisabled, { ebayChecks } from "../../utils/checkEbayDisabled";
import { daysSince } from "../../utils/listingAge";
import { computeNextStep, INextStep } from "../../utils/listingNextStep";
import { computePriceDelta, IPriceDelta } from "../../utils/listingPriceDelta";
import { computeListingScore, IListingScore } from "../ebayListings/listedTable/listingScore";

export interface IQueueRowSignals {
  offer: IEbayOffer | undefined;
  livePrice: number | null;
  /** Days since the part was first listed - does not reset on relist. */
  firstListedDays: number | null;
  /** Days the listing that is live now has been up. */
  listingDays: number | null;
  relistCount: number;
  hasPartNumbers: boolean;
  imageCount: number | null;
  score: IListingScore;
  delta: IPriceDelta;
  /** The sold summary the price chip compares against: same part number when there is one, else same car. */
  soldSummary: IListingPriceSummary | null;
  soldBasis: "partNumber" | "vehicle" | null;
  stockByVehicle: number;
  stockByPartNumber: number;
  check: ebayChecks | null;
  relistBlocked: boolean;
  relistErrors: string[];
  nextStep: INextStep;
}

const hasSold = (summary: IListingPriceSummary | null | undefined): summary is IListingPriceSummary =>
  !!summary && summary.count > 0 && summary.average != null && summary.average > 0;

export function livePriceOf(offer: IEbayOffer | undefined): number | null {
  if (!offer) return null;
  const price = offer.format === FormatType.Auction ? offer.auctionStartPrice : offer.buyItNowPrice;
  return price ?? null;
}

/**
 * Everything the queue row and the focus drawer derive from one Listed row: ages, the price gap,
 * the listing score, the relist check and the next step. Pure apart from the clock, so the same
 * part always gets the same answer in the row and in the drawer.
 */
export function computeQueueRowSignals(part: IPart, now: DateTime = DateTime.now()): IQueueRowSignals {
  const offer = part.offers?.[0];
  const livePrice = livePriceOf(offer);
  const insights = part.marketInsights ?? null;

  const firstListedDays = daysSince(part.originalListedDate ?? offer?.listingStarted ?? part.listedDate, now);
  const listingDays = daysSince(offer?.listingStarted ?? part.listedDate, now);

  const imageCount = part.imageCount ?? (Array.isArray(part.images) ? part.images.length : null);
  const score = computeListingScore(part, { imageCount });

  const soldBasis = hasSold(insights?.soldByPartNumber)
    ? "partNumber"
    : hasSold(insights?.soldByVehicle)
      ? "vehicle"
      : null;
  const soldSummary =
    soldBasis === "partNumber" ? insights!.soldByPartNumber : soldBasis === "vehicle" ? insights!.soldByVehicle : null;

  const delta = computePriceDelta(livePrice, part.intelligenceAverage, soldSummary?.average ?? null);

  let check: ebayChecks | null = null;
  if (Array.isArray(part.images) && Array.isArray(part.offers) && offer) {
    try {
      check = checkEbayDisabled({
        part,
        offer,
        partCategoryDefault: part.category?.partCategoryDefaults?.[0] ?? null,
        ignoreListingTime: true,
      });
    } catch {
      check = null;
    }
  }

  const relistErrors = check?.errors?.length ? check.errors : check?.error ? [check.error] : [];
  const relistBlocked = !!check?.isDisabled && relistErrors.length > 0;

  const hasPartNumbers = !!part.partNumber?.trim() || !!part.oePartNumber?.trim();

  const nextStep = computeNextStep({
    relistBlocked,
    hasPartNumbers,
    missingFixable: score.missing,
    priceDelta: delta,
    listingAgeDays: listingDays ?? firstListedDays,
    stockByVehicle: insights?.stockByVehicle ?? 0,
  });

  return {
    offer,
    livePrice,
    firstListedDays,
    listingDays,
    relistCount: part.relistCount ?? 0,
    hasPartNumbers,
    imageCount,
    score,
    delta,
    soldSummary,
    soldBasis,
    stockByVehicle: insights?.stockByVehicle ?? 0,
    stockByPartNumber: insights?.stockByPartNumber ?? 0,
    check,
    relistBlocked,
    relistErrors,
    nextStep,
  };
}

/** The Listed-row-only fields to keep when a full part record replaces the row in the drawer. */
export function mergeRowInsights(row: IPart, full: IPart | undefined): IPart {
  if (!full) return row;
  return {
    ...full,
    originalListedDate: full.originalListedDate ?? row.originalListedDate,
    relistCount: row.relistCount,
    relistingRule: row.relistingRule,
    marketInsights: row.marketInsights,
    imageCount: Array.isArray(full.images) ? full.images.length : row.imageCount,
    intelligenceFoundCount: row.intelligenceFoundCount,
    intelligenceKeptCount: row.intelligenceKeptCount,
    listingReviewedOn: row.listingReviewedOn,
    listingReviewedBy: row.listingReviewedBy,
    intelligenceAverage: full.intelligenceAverage ?? row.intelligenceAverage,
    intelligenceMin: full.intelligenceMin ?? row.intelligenceMin,
    intelligenceMax: full.intelligenceMax ?? row.intelligenceMax,
    lastIntelligenceLookup: full.lastIntelligenceLookup ?? row.lastIntelligenceLookup,
    batchItem: row.batchItem,
    offerUpdate: row.offerUpdate,
  };
}
